import React, { useCallback, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { Grid, SideSheet } from 'ui-toolkit';
import { ICreateInternalContactRequest } from '../../../../types/contacts/CreateInternalContact';
import { SideSheetFormFooter } from '../../../shared/SideSheetFormFooter';
import { internalOrderContactsForm } from '../../../../validation/order/InternalOrderContactsForm';
import { FormStatusWrapper } from '../../../shared/FormStatusWrapper/FormStatusWrapper';
import {
  eContactEnvironment,
  eContactType,
  getContactDuplicationMessage,
} from '../../../../utilities/contact/duplicationErrorMessage';
import { eAdminAccountStatus, IAdminUserData } from '../../../../types/administration/userAdministration/IAdminUser';
import { createDefaultPageData } from '../../../../types/rest/IPageData';
import { AddContactFormAdminSection2 } from './AddContactFormAdminSection2';
import { checkForMutationSuccess } from '../../../../API/checkForMutationSuccess';
import { useCreateInternalOrderContactMutation } from '../../../../API/order/contacts/InternalContactsApi';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../types/shared/UUID';
import { convertToError } from '../../../../API/BaseApi';
import { useListPagedAdminUsersQuery } from '../../../../API/administration/AdminUsersAPI';
import { OrderContactNotesField } from '../subComponents/OrderContactNotesField';
import { SelectInternalContactRoles } from './SelectInternalContactRoles';
import { useListRolesQuery } from '../../../../API/roles/RolesApi';

interface IAddInternalOrderContactFormProps {
  onSelectAdmin: (adminId: UUID) => void;
  onCancel: () => void;
}

export const AddInternalOrderContactForm: React.FC<IAddInternalOrderContactFormProps> = ({
  onCancel,
  onSelectAdmin,
}) => {
  const { orderId } = useParams<{ orderId: UUID }>();

  const [
    addInternalOrderContact,
    {
      error: addInternalOrderContactError,
      isSuccess: addInternalOrderContactSuccess,
      isLoading: addInternalOrderContactLoading,
    },
  ] = useCreateInternalOrderContactMutation();

  const {
    data: adminUsersData = defaultData,
    isSuccess: getAdminUsersSuccess,
    error: getAdminUsersError,
  } = useListPagedAdminUsersQuery({ status: eAdminAccountStatus.Active, fetchAll: true });

  const { data: roles = [] } = useListRolesQuery();

  const validationSchema = useMemo(
    () => internalOrderContactsForm(roles.map((role) => role.name)),
    [roles],
  );

  const onSubmit = useCallback(
    async (createInternalContactRequest: ICreateInternalContactRequest) => {
      const result = await addInternalOrderContact({ orderId, contact: createInternalContactRequest });
      checkForMutationSuccess(result, 'Add Internal Contact');
    },
    [orderId, addInternalOrderContact]
  );

  return (
    <Formik
      initialValues={initialValues}
      validateOnMount
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      <Form>
        <SideSheet
          title={'New Contact'}
          onClose={onCancel}
          footer={<SideSheetFormFooter onCancel={onCancel} saveButtonText={'Save and Add'} />}
        >
          <Grid>
            <FormStatusWrapper
              fetching={addInternalOrderContactLoading}
              fetchedSuccess={addInternalOrderContactSuccess}
              successMessage={'Contact Added!'}
              fetchError={convertToError(getAdminUsersError ?? addInternalOrderContactError)}
              duplicateMessage={getContactDuplicationMessage(eContactType.Internal, eContactEnvironment.Order)}
            />
            <AddContactFormAdminSection2
              onSelectAdmin={onSelectAdmin}
              adminUsersData={adminUsersData}
              getAdminUsersSuccess={getAdminUsersSuccess}
            />
            <SelectInternalContactRoles />
            <OrderContactNotesField name={'notes'} />
          </Grid>
        </SideSheet>
      </Form>
    </Formik>
  );
};

const initialValues: ICreateInternalContactRequest = {
  adminId: '',
  roles: [],
};

const defaultData = createDefaultPageData<IAdminUserData>();
